import { cloneDeep } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  CardCheckbox,
  CardDateTime,
  CardDateTimePicker,
  CardTextArea,
  CardTZSelect
} from '../../../../Elements';
import { AppConsumer } from '../../../../App/AppComponent';
import moment from 'moment-timezone';
import DateUtils from '../../../../../lib/utils/date';

let blurringStartsAt = false;

class ConfirmEventDetails extends Component {
  static propTypes = {
    event: PropTypes.object.isRequired,
    relay: PropTypes.object,
    submitted: PropTypes.bool.isRequired,
    viewer: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.eventFieldChange = this.eventFieldChange.bind(this);
    this.allDayChange = this.allDayChange.bind(this);
    this.startAtChange = this.startAtChange.bind(this);
    this.endAtChange = this.endAtChange.bind(this);
    this.tzChange = this.tzChange.bind(this);
    this.generateKey = this.generateKey.bind(this);
    this.handleBlurringStartsAt = this.handleBlurringStartsAt.bind(this);
    this.state = {
      event: this.getEventDefaults(),
      endAtKey: this.generateKey()
    };
  }

  getEventDefaults() {
    const event = cloneDeep(this.props.event);
    const hasTimes = event.start_at && event.end_at;
    if (!hasTimes && event.user_event) {
      event.start_at = event.user_event.start_at;
      event.end_at = event.user_event.end_at;
      event.time_zone = event.user_event.time_zone;
      event.all_day = event.user_event.start_at && event.user_event.start_at.length === 10;
    }
    event.time_zone =
      event.time_zone ||
      (this.props.viewer && this.props.viewer.s_time_zone) ||
      moment.tz.guess();
    return event;
  }

  endAtChange(e) {
    this.eventFieldChange('end_at', e.target.value);
  }

  startAtChange(e) {
    this.eventFieldChange('start_at', e.target.value);
    if (
      (!this.state.event.end_at ||
        moment(this.state.event.end_at) <=
          moment(e.target.value)) &&
      blurringStartsAt
    ) {
      const isDate = e.target.value.length === 10;
      if (isDate && moment(e.target.value, moment.ISO_8601).isValid()) {
        this.eventFieldChange('end_at', e.target.value);
      } else if (moment(e.target.value).isValid()) {
        this.eventFieldChange(
          'end_at',
          moment(e.target.value)
            .add(4, 'hours')
            .toDate()
            .toISOString()
        );
        this.setState({ endAtKey: this.generateKey() });
      }
    }
  }

  handleBlurringStartsAt(blurring) {
    blurringStartsAt = blurring;
  }

  tzChange(e, { value }) {
    const { all_day, start_at, end_at, time_zone } = this.state.event;
    if (moment(start_at, moment.ISO_8601).isValid()) {
      this.eventFieldChange(
        'start_at',
        DateUtils.dateToISOString(start_at, all_day, time_zone)
      );
    }
    if (moment(end_at, moment.ISO_8601).isValid()) {
      this.eventFieldChange(
        'end_at',
        DateUtils.dateToISOString(end_at, all_day, time_zone)
      );
    }
    this.eventFieldChange('time_zone', value);
  }

  allDayChange(allDay) {
    this.eventFieldChange('all_day', allDay);
    const timeZone = this.state.event.time_zone;
    if (moment(this.state.event.start_at).isValid()) {
      const startAt = DateUtils.dateToISOString(
        this.state.event.start_at,
        allDay,
        timeZone
      );
      this.eventFieldChange('start_at', startAt);
    }
    if (moment(this.state.event.end_at).isValid()) {
      const endAt = DateUtils.dateToISOString(
        this.state.event.end_at,
        allDay,
        timeZone
      );
      this.eventFieldChange('end_at', endAt);
    }
  }

  generateKey() {
    return `${Math.random().toString(36)}00000000000000000`.slice(2, 16 + 2);
  }

  textFieldChange(e) {
    this.eventFieldChange(e.target.name, e.target.value);
  }

  eventFieldChange(key, value) {
    this.setState(previousState => {
      const obj = cloneDeep(previousState);
      obj.event[key] = value;
      return obj;
    });
  }

  render() {
    const { endAtKey, event } = this.state;
    const { submitted } = this.props;
    const { viewer } = this.props;
    return (
      <AppConsumer>
        {({ isComputer }) =>
          <div>
            <div style={{ marginBottom: '4em' }}>
              <h3>Please confirm event details.</h3>
              <p>
                This information will be shared with any collaborators in this
                workspace.
              </p>
            </div>
            <div>
              <CardTextArea
                autoFocus
                label='Event Title'
                name='title'
                placeholder={"Jane's wedding"}
                value={event.title || ''}
                onChange={this.eventFieldChange}
                submitted={submitted}
                validationError='Event title is required'
                required
              />
              <CardCheckbox
                label='All-day'
                name='all_day'
                checked={event.all_day || false}
                onChange={this.allDayChange}
                submitted={submitted}
              />
              {!isComputer &&
                <div style={{ margin: '1em 0' }}>
                  <CardDateTime
                    label='Starts'
                    name='start_at'
                    placeholder='Start Time'
                    timeZone={event.time_zone}
                    value={event.start_at || ''}
                    dateOnly={event.all_day || false}
                    onChange={this.startAtChange}
                    submitted={submitted}
                  />
                  <CardDateTime
                    label='Ends'
                    name='end_at'
                    placeholder='End Time'
                    timeZone={event.time_zone}
                    value={event.end_at || ''}
                    dateOnly={event.all_day || false}
                    onChange={this.endAtChange}
                    submitted={submitted}
                    key={endAtKey}
                    validationError='Event must end later than it starts'
                  />
                </div>}
              {isComputer &&
                <div style={{ margin: '1em 0' }}>
                  <CardDateTimePicker
                    label='Starts'
                    name='start_at'
                    datePlaceholder='Start Date'
                    timePlaceholder='Start Time'
                    dateOnly={event.all_day}
                    value={event.start_at || ''}
                    timeZone={event.time_zone}
                    onChange={this.startAtChange}
                    viewer={viewer}
                    submitted={submitted}
                    handleBlurringDatePicker={this.handleBlurringStartsAt}
                  />
                  <CardDateTimePicker
                    label='Ends'
                    name='end_at'
                    datePlaceholder='End Date'
                    timePlaceholder='End Time'
                    dateOnly={event.all_day}
                    key={endAtKey}
                    value={event.end_at || ''}
                    timeZone={event.time_zone}
                    onChange={this.endAtChange}
                    viewer={viewer}
                    submitted={submitted}
                    validationError='Event must end later than it starts'
                  />
                </div>}
              <CardTZSelect
                label='Time Zone'
                name='time_zone'
                value={event.time_zone}
                onChange={this.tzChange}
                submitted={submitted}
                validationError='Time zone is required'
              />
            </div>
          </div>}
      </AppConsumer>
    );
  }
}

export default ConfirmEventDetails;



// WEBPACK FOOTER //
// ./webpack/components/Event/Show/Workspace/Share/ConfirmEventDetails.js